// src/shared/ui/FileDropzone.jsx
//
// Consolidates 4 hand-rolled drag/drop file-upload implementations that all
// duplicated the same state (dragging / uploading / file list), the same
// validateFiles/formatSize helpers, and the same JSX structure:
//   compliance/components/UploadCard.jsx      (single SOW + optional template)
//   compliance/components/RaidUploadCard.jsx  (single SOW)
//   compliance/components/TvrUploadCard.jsx   (single SOW)
//   golive/components/ui/FileUpload.jsx       (multi-file, with remove list)
//
// Two visual themes are supported so existing pages keep their look without
// a CSS rewrite:
//   theme="legacy"   -> compliance's hand-written CSS classes (c-card,
//                       c-dropzone, dz-*, c-btn-primary...)
//   theme="tailwind" -> golive's Tailwind utility classes
//
// Two usage modes:
//   - Uncontrolled + onSubmit: dropzone manages its own file list and shows
//     a submit button (compliance's pattern). onSubmit(files, secondaryFile)
//     is only called when the user clicks submit.
//   - Controlled + onChange: parent owns the file list, no submit button —
//     every change is reported immediately (golive's pattern).

import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { UploadCloud, FileText, X } from "lucide-react";

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getExtension(file) {
  return file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
}

export default function FileDropzone({
  theme = "tailwind",
  multiple = false,
  accept = [".pdf", ".docx"],
  maxTotalSize = 50 * 1024 * 1024,
  files: controlledFiles,
  onChange,
  onSubmit,
  submitLabel = "Upload",
  submittingLabel = "Uploading…",
  title,
  description,
  secondaryFile, // { label, accept, optional } — an extra plain <input type="file"> row (e.g. SMP's custom template)
}) {
  const inputRef = useRef(null);
  const [internalFiles, setInternalFiles] = useState(controlledFiles || []);
  const [secondary, setSecondary] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const isControlled = Array.isArray(controlledFiles);
  const currentFiles = isControlled ? controlledFiles : internalFiles;

  useEffect(() => {
    if (isControlled && controlledFiles.length > 0) setInternalFiles(controlledFiles);
  }, [controlledFiles, isControlled]);

  function validate(incoming) {
    const valid = [];
    const invalidNames = [];
    incoming.forEach((f) => {
      if (accept.includes(getExtension(f))) valid.push(f);
      else invalidNames.push(f.name);
    });
    if (invalidNames.length) {
      setError(`${invalidNames.join(", ")} — only ${accept.join(", ")} accepted.`);
    }
    return valid;
  }

  function commit(nextFiles) {
    if (!isControlled) setInternalFiles(nextFiles);
    onChange?.(nextFiles);
  }

  function addFiles(incoming) {
    setError(null);
    const valid = validate(Array.from(incoming));
    if (!valid.length) return;

    const merged = multiple ? [...currentFiles, ...valid] : [valid[0]];
    const totalBytes = merged.reduce((sum, f) => sum + f.size, 0);
    if (totalBytes > maxTotalSize) {
      setError(`Total upload size exceeds the ${(maxTotalSize / (1024 * 1024)).toFixed(0)} MB limit.`);
      return;
    }
    commit(merged);
  }

  function removeFile(name) {
    setError(null);
    commit(currentFiles.filter((f) => f.name !== name));
    if (inputRef.current) inputRef.current.value = "";
  }

  function handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    addFiles(e.dataTransfer.files);
  }
  function handleDragOver(e) { e.preventDefault(); e.stopPropagation(); setDragging(true); }
  function handleDragLeave(e) { if (!e.currentTarget.contains(e.relatedTarget)) setDragging(false); }
  function handleInputChange(e) { addFiles(e.target.files); e.target.value = null; }
  function handleZoneClick() { inputRef.current?.click(); }
  function handleZoneKeyDown(e) { if (e.key === "Enter" || e.key === " ") inputRef.current?.click(); }

  async function handleSubmit() {
    if (!currentFiles.length || submitting || !onSubmit) return;
    setSubmitting(true);
    try {
      await onSubmit(multiple ? currentFiles : currentFiles[0], secondary);
    } finally {
      setSubmitting(false);
    }
  }

  const acceptAttr = accept.join(",");

  // ── Legacy (compliance) theme ─────────────────────────────────────────
  if (theme === "legacy") {
    const singleFile = currentFiles[0];
    const dzClass = ["c-dropzone", dragging ? "drag-over" : "", singleFile ? "has-file" : ""].filter(Boolean).join(" ");

    return (
      <div className="c-card">
        {title && (
          <div className="c-card-title">
            <UploadCloud size={16} /> {title}
          </div>
        )}
        {description && <div className="c-card-desc">{description}</div>}

        <div
          className={dzClass}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleZoneClick}
          onKeyDown={handleZoneKeyDown}
          role="button"
          tabIndex={0}
          aria-label={title || "Upload document"}
        >
          <input ref={inputRef} type="file" accept={acceptAttr} multiple={multiple} onChange={handleInputChange} style={{ display: "none" }} />
          {singleFile ? (
            <div className="dz-file-info">
              <span className="dz-file-icon"><FileText size={26} /></span>
              <div>
                <div className="dz-file-name">{singleFile.name}</div>
                <div className="dz-file-change">{formatSize(singleFile.size)} &nbsp;·&nbsp; Click to change</div>
              </div>
            </div>
          ) : (
            <div>
              <div className="dz-icon"><UploadCloud size={30} /></div>
              <div className="dz-text">Drop your file here, or click to browse</div>
              <div className="dz-hint">{accept.join(" / ")} &nbsp;· up to {(maxTotalSize / (1024 * 1024)).toFixed(0)} MB</div>
            </div>
          )}
        </div>

        {secondaryFile && (
          <div className="c-template-row">
            <label htmlFor="fdz-secondary">{secondaryFile.label}</label>
            <input
              type="file"
              id="fdz-secondary"
              accept={secondaryFile.accept}
              onChange={(e) => setSecondary(e.target.files[0] || null)}
            />
            {secondaryFile.optional && <span className="c-badge-optional">Optional</span>}
          </div>
        )}

        {error && <p className="c-card-desc" style={{ color: "#dc2626" }}>{error}</p>}

        {onSubmit && (
          <button className="c-btn-primary" disabled={!singleFile || submitting} onClick={handleSubmit}>
            {submitting ? <><span className="c-spinner c-spinner-white" /> {submittingLabel}</> : submitLabel}
          </button>
        )}
      </div>
    );
  }

  // ── Tailwind theme ─────────────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-1.5">
      <div
        role="button"
        tabIndex={0}
        onClick={handleZoneClick}
        onKeyDown={handleZoneKeyDown}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={() => setDragging(false)}
        className={[
          "flex items-center justify-center gap-3 rounded-xl border-2 border-dashed px-6 py-4 transition-colors duration-150 cursor-pointer",
          dragging ? "border-blue-400 bg-blue-50" : "border-gray-200 bg-gray-50 hover:border-blue-500 hover:bg-blue-50",
        ].join(" ")}
      >
        <input ref={inputRef} type="file" accept={acceptAttr} multiple={multiple} className="hidden" onChange={handleInputChange} />
        <UploadCloud size={22} className="shrink-0 text-slate-400" />
        <div>
          <p className="text-sm font-medium text-slate-700">
            Drag &amp; drop or <span className="text-blue-600 underline underline-offset-2">browse</span>
          </p>
          <p className="text-xs text-slate-400 mt-0.5">Supported formats: {accept.join(", ")}</p>
        </div>
      </div>

      {currentFiles.length > 0 && (
        <ul className="flex flex-col gap-1.5 mt-1">
          {currentFiles.map((f) => (
            <li key={f.name} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-violet-50 border border-violet-100">
              <FileText size={16} className="shrink-0 text-blue-600" />
              <span className="flex-1 min-w-0 text-sm text-slate-800 truncate">{f.name}</span>
              <span className="text-xs text-slate-400 shrink-0">{formatSize(f.size)}</span>
              <button
                type="button"
                onClick={() => removeFile(f.name)}
                className="shrink-0 p-1 rounded-full text-slate-400 hover:text-slate-700 hover:bg-gray-200 transition-colors"
                aria-label={`Remove ${f.name}`}
              >
                <X size={13} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {error && <p className="text-xs text-red-500 mt-0.5">{error}</p>}

      {onSubmit && (
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!currentFiles.length || submitting}
          className="mt-1 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium disabled:opacity-40 hover:bg-blue-700 transition-colors"
        >
          {submitting ? submittingLabel : submitLabel}
        </button>
      )}
    </div>
  );
}

FileDropzone.propTypes = {
  theme: PropTypes.oneOf(["legacy", "tailwind"]),
  multiple: PropTypes.bool,
  accept: PropTypes.arrayOf(PropTypes.string),
  maxTotalSize: PropTypes.number,
  files: PropTypes.array,
  onChange: PropTypes.func,
  onSubmit: PropTypes.func,
  submitLabel: PropTypes.string,
  submittingLabel: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.node,
  secondaryFile: PropTypes.shape({
    label: PropTypes.string,
    accept: PropTypes.string,
    optional: PropTypes.bool,
  }),
};
